import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Trophy, Recycle } from 'lucide-react';
import TextType from './TextType';
import { Link } from 'react-router-dom';

export const ProductCategories: React.FC = () => {
  return (
    <section className="py-32 bg-white">
      <div className="container mx-auto px-4">
        <motion.div 
          className="flex flex-col md:flex-row justify-between items-end mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <div>
            <h2 
              className="mb-4"
              style={{
                fontFamily: 'Nanum Myeongjo',
                fontWeight: 700,
                fontSize: '52px',
                lineHeight: '67.6px',
                letterSpacing: '0%',
                color: '#620131'
              }}
            >
              <TextType
                baseOpacity={0}
                enableBlur={true}
                baseRotation={5}
                blurStrength={10}
                size="lg"
                duration={1}
                textClassName="font-bold text-[32px] md:text-[52px] leading-[40px] md:leading-[67.6px] text-[#620131]"
              >
                Premium Packaging
              </TextType>
              <TextType
                baseOpacity={0}
                enableBlur={true}
                baseRotation={5}
                blurStrength={10}
                size="lg"
                duration={1}
                textClassName="font-bold text-[32px] md:text-[52px] leading-[40px] md:leading-[67.6px] text-[#620131]"
              >
                We Craft.
              </TextType>
            </h2>
          </div>
          <div className="flex items-center gap-4 mt-4 md:mt-0">
             <img src="/Images/s2_client.webp" alt="Happy Clients" className="h-12 object-contain" />
             <div>
               <div className="flex text-orange-400">
                 {[1, 2, 3, 4, 5].map((i) => (
                   <Star key={i} size={16} fill="currentColor" />
                 ))}
               </div>
               <span className="text-sm font-medium text-text-muted">Happy Clients</span>
             </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Large Card */}
          <motion.div 
            whileHover={{ y: -5 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="cursor-target rounded-3xl relative overflow-hidden min-h-[500px] group"
          >
            {/* High Quality Badge */}
            <div className="absolute top-6 left-6 z-20 bg-transparent rounded-xl p-3 flex items-center gap-3 shadow-sm max-w-[200px]">
              <div className="bg-[#FF3055] p-2 rounded-lg text-white flex items-center justify-center">
                <Trophy size={18} fill="currentColor" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-gray-900 leading-tight mb-0.5" style={{ fontFamily: 'Nanum Myeongjo' }}>High Quality</h4>
                <p className="text-[10px] text-gray-500 leading-tight font-medium">Crafted from top materials</p>
              </div>  
            </div>

            <img 
              src="/Images/Image.webp" 
              alt="Premium Packaging" 
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            
            {/* Infinite Scroll Text Overlay */}
            <div className="absolute bottom-8 left-0 right-0 overflow-hidden whitespace-nowrap py-4 bg-white/10 backdrop-blur-md border-t border-white/20 border-b">
              <motion.div 
                className="inline-block text-white font-bold text-2xl"
                style={{ fontFamily: 'Nanum Myeongjo' }}
                animate={{ x: ["0%", "-50%"] }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              >
                monocarton • luxury • packaging • ecofriendly • monocarton • luxury • packaging • ecofriendly • 
              </motion.div>
              <motion.div 
                className="inline-block text-white font-bold text-2xl"
                style={{ fontFamily: 'Nanum Myeongjo' }}
                animate={{ x: ["0%", "-50%"] }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              >
                monocarton • luxury • packaging • ecofriendly • monocarton • luxury • packaging • ecofriendly • 
              </motion.div>
            </div>
          </motion.div>

          {/* Right Column Cards */}
          <div className="flex flex-col gap-6">
            {/* Top Right Card */}
            <motion.div 
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="cursor-target rounded-3xl relative overflow-hidden flex-1 min-h-[240px] group bg-[#F2D9C5]"
            >
              <div className="flex h-full">
                <div className="flex-1 p-8 flex flex-col justify-center z-10">
                  <div className="text-[#8B2D0C] mb-4">
                    <Recycle size={32} strokeWidth={1.5} />
                  </div>
                  <h3 
                    style={{
                      fontFamily: 'Nanum Myeongjo',
                      fontWeight: 800,
                      fontSize: '42px',
                      lineHeight: '46.8px',
                      letterSpacing: '0%',
                      color: '#8B2D0C'
                    }}
                  >
                    Eco Friendly <br/>
                    <span style={{ fontWeight: 700 }}>Packaging</span>
                  </h3>
                  <p 
                    className="mt-4 max-w-[200px]"
                    style={{
                      fontFamily: 'Instrument Sans',
                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: '20.8px',
                      letterSpacing: '0%',
                      color: '#C35631'
                    }}
                  >
                    Sustainable, recyclable packaging that protects nature responsibly.
                  </p>
                </div>
                <div className="flex-1 relative h-full min-h-[240px]">
                  <img 
                    src="/Images/s2_two.webp" 
                    alt="Eco Friendly Packaging" 
                    className="absolute inset-0 w-full h-full object-contain object-right-bottom transform group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            </motion.div>

            {/* Bottom Right Card */}
            <motion.div 
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="cursor-target rounded-3xl relative overflow-hidden flex-1 min-h-[240px] group bg-secondary"
            >
              <div className="flex h-full">
                <div className="flex-1 relative h-full min-h-[240px]">
                  <img 
                    src="/Images/s2_three.webp" 
                    alt="Packaging That Works" 
                    className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="flex-1 p-8 flex flex-col justify-center z-10 pl-4">
                  <h3 
                    style={{
                      fontFamily: 'Nanum Myeongjo',
                      fontWeight: 800,
                      fontSize: '42px',
                      lineHeight: '46.8px',
                      letterSpacing: '0%',
                      color: '#FFFFFF'
                    }}
                  >
                    Packaging <br/> That Works
                  </h3>
                  <p 
                    className="mt-4 mb-8"
                    style={{
                      fontFamily: 'Instrument Sans',
                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: '20.8px',
                      letterSpacing: '0%',
                      color: '#FFFFFF'
                    }}
                  >
                    Mono-cartons crafted for every category and industry.
                  </p>
                  <Link to="/products" className="flex items-center gap-3">
                    <button className="cursor-target bg-white text-text-main px-6 py-3 rounded-full font-bold text-sm hover:bg-gray-100 transition-colors">
                      Explore more Products
                    </button>
                    <div className="cursor-target w-10 h-10 bg-white rounded-full flex items-center justify-center text-[#FF8C42] cursor-pointer hover:bg-gray-100 transition-colors">
                      <ArrowRight size={20} />
                    </div>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
